// Extracted verbatim from the v3 monolith (see BIRDSEYE.md module split plan).

import { PF_VERSION, DATA_WRAPPER, DATA_FILTERS_WRAPPER, DATA_LIST, DATA_FILTER, DATA_SEARCH, DATA_RESET, DATA_EMPTY, DATA_LOADING, DATA_COUNT, DATA_PAGE, DATA_LOAD_BUTTON, DATA_LOGIC_TOGGLE, DATA_TAGS, DATA_FACET_COUNT, ANIMATION_MODES, VIEW_MODES, LOAD_MODES, ANIMATION_PRESETS, PRESETS } from './constants.js';
import { toArray, parseJsonSafe, toInt, toNonNegativeInt, toFloat, toBool, resolveAnimationPreset, parseCsv, normalizeText, debounce, callHook, dispatchEventSafe, escapeRegex, fetchWithRetry } from './utils.js';
import { normalizeFilterControls, neutralizeFacetCountInteraction } from './controls.js';
import { setPreloadState } from './ui-state.js';

import * as itemsMethods from './items.js';
import * as tagsMethods from './tags.js';
import * as facetsMethods from './facets.js';
import * as paginationMethods from './pagination.js';
import * as eventsMethods from './events.js';
import * as stateMethods from './state.js';
import * as matchingMethods from './matching.js';
import * as animationMethods from './animation.js';
import * as renderMethods from './render.js';
import * as urlsyncMethods from './urlsync.js';
import * as loadingMethods from './loading.js';
import * as apiMethods from './api.js';
import { getDiagnostics, _reportSetupDiagnostics } from './diagnostics.js';
import { discoverStructure, describeNode } from './discovery.js';

var PF_INSTANCE_UID = 0;

function PandaFilter(wrapper, options) {
  if (!wrapper || !(wrapper instanceof Element)) {
    throw new Error('[PandaFilter] wrapper must be a DOM element');
  }

  this.wrapper = wrapper;
  this.version = PF_VERSION;
  this.__pfVersion = PF_VERSION;
  this.options = options || {};
  this.hooks = Object.assign({}, this.options.hooks || {}, this.wrapper.pfHooks || {});
  this._uid = PF_INSTANCE_UID + 1;
  PF_INSTANCE_UID = this._uid;

  // v4: explicit attributes win; safe Webflow structure is inferred when they are
  // absent; ambiguity fails loud instead of guessing (see BIRDSEYE.md).
  this.discovery = discoverStructure(this.wrapper);
  this.list = this.discovery.list.node;
  this.itemSelector = this.discovery.item.selector;

  if (!this.list) {
    if (this.discovery.list.source === 'ambiguous') {
      throw new Error(
        '[PandaFilter] Ambiguous result list: found ' +
        this.discovery.list.candidates.length +
        ' Webflow list candidates inside the wrapper and no [data-pf="list"]. ' +
        'Add data-pf="list" to the intended result list, or run assisted setup ' +
        '(Webflow MCP). Candidates: ' +
        this.discovery.list.candidates.map(describeNode).join(' | ')
      );
    }

    throw new Error(
      '[PandaFilter] Missing [data-pf="list"] inside wrapper and no inferable Webflow ' +
      'list (.w-dyn-list > .w-dyn-items). Add data-pf="list" to the result list, or ' +
      'run assisted setup (Webflow MCP).'
    );
  }

  this.instanceIndex = Number.isFinite(this.options.instanceIndex)
    ? this.options.instanceIndex
    : 0;
  this.instanceKey =
    this.wrapper.getAttribute('data-pf-url-key') ||
    this.wrapper.id ||
    'instance' + (this.instanceIndex + 1);

  // data-pf-preset resolves to a bundle of defaults; an explicit data-pf-* attribute on
  // the wrapper always wins. pfAttr(name, presetKey) returns the explicit value, else the
  // preset's value (as a string), else null so the existing fallback applies.
  this.presetName = normalizeText(this.wrapper.getAttribute('data-pf-preset') || '');
  // Own keys only: a name like "constructor" must not resolve to Object.prototype members.
  var preset = Object.prototype.hasOwnProperty.call(PRESETS, this.presetName) ? PRESETS[this.presetName] : {};
  var wrapper = this.wrapper;
  function pfAttr(name, presetKey) {
    var explicit = wrapper.getAttribute(name);
    if (explicit !== null) {
      return explicit;
    }
    if (presetKey && preset[presetKey] !== undefined) {
      return String(preset[presetKey]);
    }
    return null;
  }

  this.defaultLogic = normalizeText(pfAttr('data-pf-logic', 'logic') || 'and') === 'or' ? 'or' : 'and';
  this.currentLogic = this.defaultLogic;

  this.facetCountMode = normalizeText(this.wrapper.getAttribute('data-pf-facet-count-mode') || 'filtered');
  if (this.facetCountMode !== 'filtered' && this.facetCountMode !== 'global') {
    this.facetCountMode = 'filtered';
  }
  this.facetAccuracy = normalizeText(this.wrapper.getAttribute('data-pf-facet-accuracy') || 'partial');
  if (this.facetAccuracy !== 'strict') {
    this.facetAccuracy = 'partial';
  }
  this.facetHideEmpty = toBool(this.wrapper.getAttribute('data-pf-facet-hide-empty'), false);
  this.facetDisableEmpty = toBool(this.wrapper.getAttribute('data-pf-facet-disable-empty'), false);
  // One class token: classList.toggle throws on a value with spaces, which would break every render.
  this.facetEmptyClass = (this.wrapper.getAttribute('data-pf-facet-empty-class') || '').trim().split(/\s+/)[0] || 'is-empty';

  var hasFacetNodes = !!this.wrapper.querySelector(DATA_FACET_COUNT);
  this.forceFullDataset = toBool(
    this.wrapper.getAttribute('data-pf-force-full-dataset'),
    toBool(this.wrapper.getAttribute('data-pf-require-full-dataset'), false)
  );
  if (!this.forceFullDataset && hasFacetNodes && this.facetCountMode === 'global' && this.facetAccuracy === 'strict') {
    this.forceFullDataset = true;
  }

  this.pageSize = toInt(pfAttr('data-pf-pagesize', 'pagesize'), 12);
  this.mode = normalizeText(pfAttr('data-pf-mode', 'mode') || 'pagination');
  if (VIEW_MODES.indexOf(this.mode) === -1) {
    this.mode = 'pagination';
  }
  var resolvedLoad = pfAttr('data-pf-load', 'load');
  var hasLoadAttr = resolvedLoad !== null;
  var hasPreloadAttr = this.wrapper.hasAttribute('data-pf-preload');
  this.paginationMode = 'numbers';
  this.pageSiblings = toNonNegativeInt(this.wrapper.getAttribute('data-pf-page-siblings'), 1);
  this.pageBoundary = toNonNegativeInt(this.wrapper.getAttribute('data-pf-page-boundary'), 1);
  this.pageDotsLabel = this.wrapper.getAttribute('data-pf-page-dots') || '...';

  this.loadMode = normalizeText(resolvedLoad || 'auto');
  if (LOAD_MODES.indexOf(this.loadMode) === -1) {
    this.loadMode = 'auto';
  }
  this.loadStrategy = normalizeText(this.wrapper.getAttribute('data-pf-load-strategy') || '');
  if (['fast', 'balanced', 'full'].indexOf(this.loadStrategy) === -1) {
    this.loadStrategy = '';
  }
  this.initialItemsTarget = toNonNegativeInt(this.wrapper.getAttribute('data-pf-initial-items'), 60);
  this.searchEscalate = toBool(this.wrapper.getAttribute('data-pf-search-escalate'), true);
  this.searchEscalateMin = toNonNegativeInt(this.wrapper.getAttribute('data-pf-search-escalate-min'), 2);
  this.searchEscalateText = this.wrapper.getAttribute('data-pf-progress-text') || 'In progress...';
  if (this.mode === 'pagination') {
    this.paginationMode = 'numbers';
    if (!hasLoadAttr) {
      this.loadMode = 'auto';
    }
  } else if (this.mode === 'loadmore') {
    this.paginationMode = 'loadmore';
    if (!hasLoadAttr) {
      this.loadMode = 'auto';
    }
  } else if (this.mode === 'renderall') {
    this.paginationMode = 'all';
    this.loadMode = hasLoadAttr ? this.loadMode : 'all';
  } else if (this.mode === 'infinite') {
    this.paginationMode = 'infinite';
    if (!hasLoadAttr) {
      this.loadMode = 'auto';
    }
  }
  this.preloadMode = normalizeText(this.wrapper.getAttribute('data-pf-preload') || 'none');
  if (['none', 'init', 'all'].indexOf(this.preloadMode) === -1) {
    this.preloadMode = 'none';
  }
  if (this.mode === 'renderall' && !hasPreloadAttr) {
    this.preloadMode = 'all';
  }
  if (!hasPreloadAttr && this.mode !== 'renderall') {
    this.preloadMode = 'init';
  }
  if (!hasLoadAttr && this.forceFullDataset) {
    this.loadMode = 'all';
    if (!hasPreloadAttr) {
      this.preloadMode = 'all';
    }
  }

  this.domRenderMode = normalizeText(this.wrapper.getAttribute('data-pf-dom') || '');
  if (this.domRenderMode !== 'all' && this.domRenderMode !== 'visible') {
    this.domRenderMode = this.mode === 'renderall' ? 'all' : 'visible';
  }
  this.mountVisibleOnly = this.domRenderMode === 'visible';

  if (!this.loadStrategy) {
    if (this.loadMode === 'all' || this.mode === 'renderall') {
      this.loadStrategy = 'full';
    } else if (this.loadMode === 'none' || this.loadMode === 'manual') {
      this.loadStrategy = 'fast';
    } else {
      this.loadStrategy = 'balanced';
    }
  }

  if (this.loadMode === 'all') {
    this.loadStrategy = 'full';
  } else if (this.loadMode === 'none' || this.loadMode === 'manual') {
    this.loadStrategy = 'fast';
  }

  if (this.loadMode !== 'auto') {
    this.searchEscalate = false;
  }
  this.infiniteThreshold = toFloat(this.wrapper.getAttribute('data-pf-infinite-threshold'), 0.15);

  this.urlSyncMode = normalizeText(this.wrapper.getAttribute('data-pf-url-sync') || 'off');
  this.urlRead = this.urlSyncMode === 'read' || this.urlSyncMode === 'both' || toBool(this.wrapper.getAttribute('data-pf-url-read'), false);
  this.urlWrite = this.urlSyncMode === 'write' || this.urlSyncMode === 'both' || toBool(this.wrapper.getAttribute('data-pf-url-write'), false);

  this.searchDebounce = toInt(this.wrapper.getAttribute('data-pf-search-debounce'), 180);
  this.fieldMap = parseJsonSafe(this.wrapper.getAttribute('data-pf-map'), {});
  this.tagFormat = normalizeText(this.wrapper.getAttribute('data-pf-tag-format') || 'field-value');
  this.debugEnabled = toBool(this.wrapper.getAttribute('data-pf-debug'), false);

  this.animationMode = normalizeText(this.wrapper.getAttribute('data-pf-animation') || 'flip');
  if (ANIMATION_MODES.indexOf(this.animationMode) === -1) {
    this.animationMode = 'flip';
  }
  this.animationPresetName = normalizeText(this.wrapper.getAttribute('data-pf-animation-preset') || 'smooth');
  this.animationPreset = resolveAnimationPreset(this.animationPresetName);

  var hasDuration = this.wrapper.getAttribute('data-pf-animation-duration') !== null;
  var hasStagger = this.wrapper.getAttribute('data-pf-animation-stagger') !== null;
  var hasEasing = this.wrapper.getAttribute('data-pf-animation-easing') !== null;
  var hasFadeIn = this.wrapper.getAttribute('data-pf-animation-fade-in') !== null;
  var hasY = this.wrapper.getAttribute('data-pf-animation-y') !== null;
  var hasEnterScaleFrom = this.wrapper.getAttribute('data-pf-animation-enter-scale-from') !== null;
  var hasEnterScaleTo = this.wrapper.getAttribute('data-pf-animation-enter-scale-to') !== null;
  var hasMoveScaleFrom = this.wrapper.getAttribute('data-pf-animation-move-scale-from') !== null;
  var hasMoveScaleTo = this.wrapper.getAttribute('data-pf-animation-move-scale-to') !== null;
  var hasEnterBlurFrom = this.wrapper.getAttribute('data-pf-animation-enter-blur-from') !== null;
  var hasEnterBlurTo = this.wrapper.getAttribute('data-pf-animation-enter-blur-to') !== null;

  this.animationDuration = hasDuration
    ? toInt(this.wrapper.getAttribute('data-pf-animation-duration'), this.animationPreset.duration)
    : this.animationPreset.duration;
  this.animationStagger = hasStagger
    ? toNonNegativeInt(this.wrapper.getAttribute('data-pf-animation-stagger'), this.animationPreset.stagger)
    : this.animationPreset.stagger;

  if (hasEasing) {
    this.animationEasing = this.wrapper.getAttribute('data-pf-animation-easing');
  } else {
    this.animationEasing =
      this.animationMode === 'gsap'
        ? this.animationPreset.gsapEasing
        : this.animationPreset.flipEasing;
  }

  this.animationFadeIn = hasFadeIn
    ? toBool(this.wrapper.getAttribute('data-pf-animation-fade-in'), this.animationPreset.fadeIn)
    : this.animationPreset.fadeIn;
  this.animationYOffset = hasY
    ? toFloat(this.wrapper.getAttribute('data-pf-animation-y'), this.animationPreset.y)
    : this.animationPreset.y;
  this.animationEnterScaleFrom = hasEnterScaleFrom
    ? toFloat(this.wrapper.getAttribute('data-pf-animation-enter-scale-from'), this.animationPreset.enterScaleFrom)
    : this.animationPreset.enterScaleFrom;
  this.animationEnterScaleTo = hasEnterScaleTo
    ? toFloat(this.wrapper.getAttribute('data-pf-animation-enter-scale-to'), this.animationPreset.enterScaleTo)
    : this.animationPreset.enterScaleTo;
  this.animationMoveScaleFrom = hasMoveScaleFrom
    ? toFloat(this.wrapper.getAttribute('data-pf-animation-move-scale-from'), this.animationPreset.moveScaleFrom)
    : this.animationPreset.moveScaleFrom;
  this.animationMoveScaleTo = hasMoveScaleTo
    ? toFloat(this.wrapper.getAttribute('data-pf-animation-move-scale-to'), this.animationPreset.moveScaleTo)
    : this.animationPreset.moveScaleTo;
  this.animationEnterBlurFrom = hasEnterBlurFrom
    ? toFloat(this.wrapper.getAttribute('data-pf-animation-enter-blur-from'), this.animationPreset.enterBlurFrom)
    : this.animationPreset.enterBlurFrom;
  this.animationEnterBlurTo = hasEnterBlurTo
    ? toFloat(this.wrapper.getAttribute('data-pf-animation-enter-blur-to'), this.animationPreset.enterBlurTo)
    : this.animationPreset.enterBlurTo;

  this.items = [];
  this.itemIdSeq = 0;
  this.itemKeySet = new Set();

  this.pendingPageUrls = [];
  this.loadedPageUrls = new Set();
  // Pages whose fetch failed after retries. They leave the queue (no endless retry), so
  // datasetComplete still turns true — anything that claims "absent from the whole
  // collection" must also check this is empty.
  this.failedPageUrls = new Set();
  this.isLoading = false;
  this.isFetching = false;
  this.isPreloading = false;
  this.isProgressLoading = false;
  this.autoLoadInProgress = false;
  this.infiniteObserver = null;
  this.infiniteSentinel = null;
  this.infiniteBusy = false;
  this._ownsInfiniteSentinel = false;
  this.searchEscalationInProgress = false;
  this.datasetComplete = false;
  this.datasetPrimed = false;
  this._datasetCompleteEmitted = false;

  this.filtersWrapper = this.wrapper.querySelector(DATA_FILTERS_WRAPPER);
  this.rawFilterControls = toArray(this.wrapper.querySelectorAll(DATA_FILTER));
  this.filterControls = normalizeFilterControls(this.rawFilterControls);
  this.searchControls = toArray(this.wrapper.querySelectorAll(DATA_SEARCH));
  this.resetControls = toArray(this.wrapper.querySelectorAll(DATA_RESET));
  this.logicToggles = toArray(this.wrapper.querySelectorAll(DATA_LOGIC_TOGGLE));

  this.emptyNode = this.discovery.empty.node;
  this.loadingNode = this.wrapper.querySelector(DATA_LOADING);
  this.loadingTextNode = this.wrapper.querySelector('[data-pf-loading-text]');
  if (!this.loadingTextNode && this.loadingNode && !this.loadingNode.querySelector('*')) {
    this.loadingTextNode = this.loadingNode;
  }
  this.loadingDefaultText = this.loadingTextNode ? this.loadingTextNode.textContent : '';
  this.countNodes = toArray(this.wrapper.querySelectorAll(DATA_COUNT));
  this.facetCountNodes = toArray(this.wrapper.querySelectorAll(DATA_FACET_COUNT));
  this.facetCountNodes.forEach(neutralizeFacetCountInteraction);
  this.facetCountDefs = [];
  this.tagsContainer = this.wrapper.querySelector(DATA_TAGS);
  this.tagTemplate = null;

  this.pageButtons = {
    prev: toArray(this.wrapper.querySelectorAll(DATA_PAGE + '[data-pf-page="prev"]')),
    next: toArray(this.wrapper.querySelectorAll(DATA_PAGE + '[data-pf-page="next"]')),
    numbersContainer: null,
    numberTemplate: null,
    dotsTemplate: null,
  };
  this.paginationSource = this.pageButtons.prev.length || this.pageButtons.next.length ? 'explicit' : 'missing';
  if (!this.pageButtons.prev.length) {
    this.pageButtons.prev = toArray(this.wrapper.querySelectorAll('.w-pagination-previous'));
  }
  if (!this.pageButtons.next.length) {
    this.pageButtons.next = toArray(this.wrapper.querySelectorAll('.w-pagination-next'));
  }
  if (this.paginationSource === 'missing' && (this.pageButtons.prev.length || this.pageButtons.next.length)) {
    this.paginationSource = 'webflow';
  }
  this.pageDisabledClass = this.wrapper.getAttribute('data-pf-page-disabled-class') || 'is-disabled';
  this.pageHiddenClass = this.wrapper.getAttribute('data-pf-page-hidden-class') || 'is-hidden';
  this.pageHideUnavailable = toBool(
    this.wrapper.getAttribute('data-pf-page-hide-unavailable'),
    false
  );

  this.loadButton = this.wrapper.querySelector(DATA_LOAD_BUTTON);

  this.currentPage = 1;
  this.totalPages = 0;
  this.filteredItems = [];

  this._numberClickHandler = null;
  this._numberClickCleanup = null;
  this._listeners = [];
  this._mutationObserver = null;
  this._mutationMuteCounter = 0;
  this._animationMap = new WeakMap();

  this.wrapperIndex = toArray(document.querySelectorAll(DATA_WRAPPER)).indexOf(this.wrapper);
  this.listIndex = toArray(document.querySelectorAll(DATA_LIST)).indexOf(this.list);

  this._init();
}

PandaFilter.prototype._debugLog = function (message, payload) {
  if (!this.debugEnabled) {
    return;
  }

  var prefix = '[PandaFilter][' + this.instanceKey + ']';
  if (payload === undefined) {
    console.log(prefix + ' ' + message);
    return;
  }

  console.log(prefix + ' ' + message, payload);
};

PandaFilter.prototype._init = function () {
  var self = this;

  this._collectInitialItems();
  this._setupTags();
  this._collectFacetCountDefs();
  this._setupPaginationTemplates();
  this._bindControlEvents();
  this._setupInfiniteScroll();
  this._observeListMutations();
  this._discoverPaginationUrls(document);
  this._debugLog('init:dom-scanned', {
    mode: this.mode,
    items: this.items.length,
    filters: this.filterControls.length,
    rawFilters: this.rawFilterControls.length,
    search: this.searchControls.length,
    prevButtons: this.pageButtons.prev.length,
    nextButtons: this.pageButtons.next.length,
    pendingPages: this.pendingPageUrls.length,
    loadMode: this.loadMode,
    loadStrategy: this.loadStrategy,
    preloadMode: this.preloadMode,
    paginationMode: this.paginationMode,
    domRenderMode: this.domRenderMode,
    forceFullDataset: this.forceFullDataset,
    initialItemsTarget: this.initialItemsTarget,
    facetAccuracy: this.facetAccuracy,
    urlRead: this.urlRead,
    urlWrite: this.urlWrite,
  });

  if (this.preloadMode === 'init' || this.preloadMode === 'all') {
    setPreloadState(this, true);
    this._debugLog('preload:start', { mode: this.preloadMode });
  }

  if (!this.urlRead && !this.urlWrite) {
    this._clearOwnUrlParams();
  }

  if (this.urlRead) {
    this._restoreStateFromUrl();
  }

  this.applyFilters({
    resetPage: false,
    reason: 'init',
    skipAutoEnsure: true,
  });

  if (this.preloadMode === 'init') {
    setPreloadState(this, false);
    this._debugLog('preload:end', { mode: this.preloadMode });
  }

  var initialLoadPromise = Promise.resolve();

  if (this.loadMode === 'all' || (this.loadMode === 'auto' && this.loadStrategy === 'full')) {
    this._debugLog('init:load-all:start');
    initialLoadPromise = this.loadAllPages();
  } else if (this.loadMode === 'auto') {
    if (this.loadStrategy === 'balanced') {
      this._debugLog('init:auto-load:prime', { target: this.initialItemsTarget });
      initialLoadPromise = this._primeInitialDataset();
    } else {
      this._debugLog('init:auto-load:check');
      initialLoadPromise = this.ensureEnoughItemsForCurrentView();
    }
  }

  if (this.preloadMode === 'all') {
    if (this.loadMode === 'all' || this.loadMode === 'auto') {
      initialLoadPromise.finally(function () {
        setPreloadState(self, false);
        self._debugLog('preload:end', { mode: self.preloadMode });
      });
    } else {
      setPreloadState(this, false);
      this._debugLog('preload:end', { mode: this.preloadMode });
    }
  }

  if (this.loadMode === 'manual' && this.loadButton) {
    this._listen(this.loadButton, 'click', function (event) {
      event.preventDefault();
      self._debugLog('manual-load:click');
      self.loadNextPage();
    });
    this._refreshLoadButtonState();
  }

  this._reportSetupDiagnostics();

  callHook(this.hooks.onInit, { instance: this }, this);
  dispatchEventSafe(this.wrapper, 'panda:init', {
    instance: this,
    total: this.items.length,
  });
};

PandaFilter.prototype._listen = function (el, eventName, handler, options) {
  if (!el || typeof el.addEventListener !== 'function') {
    return function () { };
  }

  el.addEventListener(eventName, handler, options);

  var cleanup = function () {
    el.removeEventListener(eventName, handler, options);
  };

  this._listeners.push(cleanup);
  return cleanup;
};


Object.assign(
  PandaFilter.prototype,
  itemsMethods,
  tagsMethods,
  facetsMethods,
  paginationMethods,
  eventsMethods,
  stateMethods,
  matchingMethods,
  animationMethods,
  renderMethods,
  urlsyncMethods,
  loadingMethods,
  apiMethods,
  {
    getDiagnostics: getDiagnostics,
    _reportSetupDiagnostics: _reportSetupDiagnostics,
  }
);

PandaFilter.initAll = function (root, options) {
  var base = root || document;
  var opts = options || {};

  return toArray(base.querySelectorAll(DATA_WRAPPER))
    .map(function (wrapper, index) {
      if (wrapper.__pandaFilterInstance) {
        var existing = wrapper.__pandaFilterInstance;
        var isSameVersion = existing && existing.__pfVersion === PF_VERSION;

        if (isSameVersion) {
          return existing;
        }

        if (existing && typeof existing.destroy === 'function') {
          try {
            existing.destroy();
          } catch (_error) {
            // Ignore stale instance cleanup errors and continue with fresh init.
          }
        }

        wrapper.__pandaFilterInstance = null;
      }

      var instance;
      try {
        instance = new PandaFilter(
          wrapper,
          Object.assign(
            {
              instanceIndex: index,
            },
            opts
          )
        );
      } catch (error) {
        // Fail loud per wrapper, but keep other wrappers on the page working. The message
        // stays on the wrapper so the debug panel can show why it has no instance.
        wrapper.__pandaFilterSetupError = String(error && error.message ? error.message : error);
        console.error(error);
        dispatchEventSafe(wrapper, 'panda:setup-error', {
          error: error,
          wrapper: wrapper,
        });
        return null;
      }

      wrapper.__pandaFilterSetupError = null;
      wrapper.__pandaFilterInstance = instance;
      return instance;
    })
    .filter(Boolean);
};

PandaFilter.reinitAll = function (root, options) {
  var base = root || document;

  toArray(base.querySelectorAll(DATA_WRAPPER)).forEach(function (wrapper) {
    if (wrapper.__pandaFilterInstance && typeof wrapper.__pandaFilterInstance.destroy === 'function') {
      wrapper.__pandaFilterInstance.destroy();
    }
  });

  return PandaFilter.initAll(base, options);
};

PandaFilter.utils = {
  parseCsv: parseCsv,
  normalizeText: normalizeText,
  debounce: debounce,
  fetchWithRetry: fetchWithRetry,
  escapeRegex: escapeRegex,
  toInt: toInt,
  toNonNegativeInt: toNonNegativeInt,
  toFloat: toFloat,
  animationPresets: ANIMATION_PRESETS,
  version: PF_VERSION,
};

PandaFilter.version = PF_VERSION;

export default PandaFilter;
